import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import {
  AddressTypes,
  Ledger,
  ScrollViewWithHeaderTitle,
  SectionHeader,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { useChallengesAndRewards } from '@order/ChallengesAndRewards';
import {
  DeliveryAddressListAction,
  RestaurantListAction,
} from '@order/components';
import { useCart } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { RewardExceptionModal } from '../RewardExceptionModal';
import { BagNavigation, BagRewardNotice, Header } from './Bag.components';
import { BagEmpty } from './Bag.empty';
import { useLedgerItems } from './Bag.hooks';
import {
  BagAddonsRail,
  BagContentBlock,
  BagFocusScope,
  BagLineItems,
  BagRewardsRail,
} from './subcomponents';

// ────────────────────────────────────────────────────────────────────────────────

export const BagContents = ({
  navigateToCheckout,
  navigateToMenu,
  canChangeLocation,
}: BagContentsProps) => {
  const { t } = useLocalizationContext();
  const { minWidth } = useResponsive();
  const { cartOrderType, isCartEmpty } = useCart();
  const { track } = useTelemetry();
  const navigation = useNavigation();

  const { applyRewardExceptionMessage, applyRewardHasNewException } =
    useChallengesAndRewards();

  React.useLayoutEffect(() => {
    navigation.setOptions({
      title: t('bag-title', { orderType: cartOrderType }),
    });
  }, [t, navigation, cartOrderType]);

  useFocusEffect(
    React.useCallback(() => {
      track('bag.view', { orderType: cartOrderType });
    }, [cartOrderType, track]),
  );

  // ─── Styles ──────────────────────────────────────────────────────

  const contentsStyle = [styles.content, minWidth.tablet && styles.contentSM];

  // ─────────────────────────────────────────────────────────────────

  return (
    <BagFocusScope>
      <ScrollViewWithHeaderTitle
        testID="bag-contents"
        style={contentsStyle}
        contentContainerStyle={styles.grow}
      >
        {isCartEmpty ? (
          <BagEmpty onPressViewMenu={navigateToMenu} />
        ) : (
          <BagInnerContents canChangeLocation={canChangeLocation} />
        )}
      </ScrollViewWithHeaderTitle>

      <BagNavigation
        navigateToCheckout={navigateToCheckout}
        navigateToMenu={navigateToMenu}
      />

      <RewardExceptionModal
        visible={applyRewardHasNewException}
        message={applyRewardExceptionMessage}
      />
    </BagFocusScope>
  );
};

export const BagInnerContents = ({
  canChangeLocation,
}: BagInnerContentsProps): React.ReactElement | null => {
  const { t } = useLocalizationContext();
  const { cart, cartOrderType } = useCart();
  const { id: orderId, ledger, restaurant, deliveryOrderDetail } = cart ?? {};

  const showTip = false;
  const ledgerItems = useLedgerItems({
    ledger,
    showTip,
    showReward: false,
  });

  return (
    <View style={styles.innerContentsWrapper}>
      <BagContentBlock>
        <Header orderType={cartOrderType} />
      </BagContentBlock>

      <BagContentBlock>
        {cartOrderType === AddressTypes.delivery ? (
          <DeliveryAddressListAction
            orderId={orderId}
            deliveryAddress={deliveryOrderDetail?.address}
            deliveryOrderDetail={deliveryOrderDetail ?? {}}
            restaurant={restaurant}
            orderType={cartOrderType}
            canChangeLocation={canChangeLocation}
          />
        ) : (
          <RestaurantListAction
            orderId={orderId}
            restaurant={restaurant}
            orderType={cartOrderType}
            canChangeLocation={canChangeLocation}
          />
        )}
      </BagContentBlock>

      <BagContentBlock>
        <BagRewardsRail />
      </BagContentBlock>

      <BagContentBlock>
        <SectionHeader heading={t('general.your-order')} />
      </BagContentBlock>

      <BagContentBlock>
        <BagLineItems />
      </BagContentBlock>

      <BagContentBlock style={styles.addonRailBlock}>
        <BagAddonsRail />
      </BagContentBlock>

      <BagContentBlock>
        <Ledger hasTopBorder items={ledgerItems} testID="bag.ledger" />
      </BagContentBlock>

      <BagContentBlock>
        <BagRewardNotice />
      </BagContentBlock>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    flexGrow: 1,
    paddingBottom: theme.spacing.xxlarge,
  },
  contentSM: {
    paddingVertical: theme.spacing.large,
  },
  grow: {
    flexGrow: 1,
  },
  innerContentsWrapper: {
    paddingBottom: theme.spacing.medium,
  },
  addonRailBlock: {
    backgroundColor: theme.colors.QUINOA,
    marginTop: theme.spacing.large,
    marginBottom: theme.spacing.xlarge,
  },
});

// ––– TYPES ––––––––––––––––––––––––––––––––––––––––––––––––––

type BagContentsProps = {
  readonly navigateToCheckout: () => void;
  readonly navigateToMenu: () => void;
} & BagInnerContentsProps;

type BagInnerContentsProps = {
  readonly canChangeLocation: () => boolean;
};
